import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { injectIntl, type WrappedComponentProps } from 'react-intl-next';
import { CONFLUENCE_ACTIVE, CONFLUENCE_LOADING } from '../state/context/types';
import { CONFTREE_CROSS_SELL, CONFTREE_ERROR, CONFTREE_NO_ACCESS_TEASER } from '../state/ui/types';

import ViewTracker from '../common/analytics/view-tracker';
import SpaceConnectionTracker from '../common/analytics/space-connection-tracker/connected';
import { TemplateProvider as TemplatePreviewProvider } from '../containers/template';
import CrossJoinAnalyticsContext from './cross-join-analytics-context/connected';
import { EmbeddedPageProvider } from '../containers/embedded-page';

import { type Props } from './types';
import { ConfluencePageTreeProvider } from '@atlassiansox/confluence-table-tree';
import { getAnalyticsWebClient } from '../common/analytics/analytics-web-client';
import { useThemeContext } from '../ui/theme';
import * as Styled from './styled';
import Footer from '../ui/powered-by-confluence-footer';
import { type Template } from '../containers/template/types';
import { getBasePreExpandTemplates } from '../ui/project-pages-improvement/constants';
import Header from './header';
import { ActivePage } from './active-page';
import { RouteProvider } from '../containers/view-router-context';
import { PageDecorators } from './page-decorators';
import GetConfluenceViewProvider from '../containers/get-confluence-view/connected';
import { CompactHeader } from './compact-header';

const View = (props: Props & WrappedComponentProps) => {
	const {
		CreateSpaceDialog,
		ConnectSpaceDialog,
		MoreMenu,
		PagesHeading,
		Flags,
		breadcrumbs,
		EmbeddedConfluenceDialog,
		triggerShowConnectSpaceDialog,
		triggerHideConnectSpaceDialog,
		confluenceLicenseStatus,
		confluenceState,
		hasConfluence,
		renderFullHeader = true,
		projectSpaceKey,
		hasAccessToConfluence,
	} = props;

	const isErrorState = useMemo(() => {
		return [CONFTREE_ERROR, CONFTREE_NO_ACCESS_TEASER].includes(confluenceLicenseStatus);
	}, [confluenceLicenseStatus]);

	const containerRef = useRef<HTMLElement>(null);
	const dataTestId = hasAccessToConfluence ? 'post-expand' : 'pre-expand';

	const handleReload = useCallback(() => window.location.reload(), []);

	const isCrossSell = confluenceLicenseStatus === CONFTREE_CROSS_SELL;

	const theme = useThemeContext();

	const shouldShowMeatballMenu = !isErrorState && confluenceState === CONFLUENCE_ACTIVE;

	return (
		<Styled.ProjectPagesContent innerRef={containerRef}>
			<Styled.ProjectPagesContentWrapper
				data-testid={`confluence-project-pages.${dataTestId}`}
				theme={theme}
			>
				<Styled.ViewContainer id="project-pages-view-container" theme={theme}>
					<Styled.ViewSpacer hasHeader={renderFullHeader}>
						{renderFullHeader ? (
							<Header
								breadcrumbs={breadcrumbs}
								isCrossSell={isCrossSell}
								MoreMenu={MoreMenu}
								shouldShowMeatballMenu={shouldShowMeatballMenu}
							/>
						) : (
							<CompactHeader MoreMenu={MoreMenu} shouldShowMeatballMenu={shouldShowMeatballMenu} />
						)}

						<Styled.MainContainer id="project-pages-main-container" theme={theme}>
							<PagesHeading key="PagesHeading" isPreExpand={isCrossSell} />
							<ActivePage {...props} />
							<ConnectSpaceDialog
								key="ConnectSpaceDialog"
								onCancel={triggerHideConnectSpaceDialog}
							/>
							<CreateSpaceDialog key="CreateSpaceDialog" onCancel={triggerShowConnectSpaceDialog} />
							<EmbeddedConfluenceDialog spaceKey={projectSpaceKey} />
							{confluenceState !== CONFLUENCE_LOADING && <ViewTracker />}
							<SpaceConnectionTracker />
							<Flags onReload={handleReload} />
						</Styled.MainContainer>
						<Footer hasAccessToConfluence={hasAccessToConfluence} />
					</Styled.ViewSpacer>
					<PageDecorators hasConfluence={hasConfluence} containerRef={containerRef} />
				</Styled.ViewContainer>
			</Styled.ProjectPagesContentWrapper>
		</Styled.ProjectPagesContent>
	);
};

const withProviders =
	<P extends object>(Component: React.ComponentType<P>): React.FC<P & Props> =>
	(props) => {
		const { triggerErrorFlag, accountId, confluenceState, triggerInitializeView } = props;
		const [activeTemplatePreview, setActiveTemplatePreview] = useState<Template | undefined>(
			getBasePreExpandTemplates()[0],
		);
		const analyticsClient = useMemo(getAnalyticsWebClient, []);

		useEffect(() => {
			triggerInitializeView();
		}, [triggerInitializeView]);

		return (
			<CrossJoinAnalyticsContext>
				<EmbeddedPageProvider>
					<ConfluencePageTreeProvider
						onError={triggerErrorFlag}
						// @ts-expect-error -  Type 'import("platform/packages/atlassian-analytics-pipeline/analytics-web-client/dist/types/analyticsWebClient").default' is not assignable to type 'import("platform/packages/confluence/confluence-table-tree/dist/types/types").AnalyticsWebClient'.
						analyticsClient={analyticsClient}
						accountId={accountId}
					>
						<AnalyticsContext
							data={{
								attributes: {
									hasConfluence: confluenceState === CONFLUENCE_ACTIVE,
								},
							}}
						>
							<TemplatePreviewProvider
								value={{
									template: activeTemplatePreview,
									setActiveTemplatePreview,
								}}
							>
								<GetConfluenceViewProvider>
									<RouteProvider {...props}>
										<Component {...props} />
									</RouteProvider>
								</GetConfluenceViewProvider>
							</TemplatePreviewProvider>
						</AnalyticsContext>
					</ConfluencePageTreeProvider>
				</EmbeddedPageProvider>
			</CrossJoinAnalyticsContext>
		);
	};

export default injectIntl<'intl', Props & WrappedComponentProps>(withProviders(View));
